import { Select, Tag } from "antd";
import React, { useEffect, useRef, useState } from "react";

import ajaxUtil from "../../util/ajax";

const { DOMAIN_CURRENT, ajaxv2 } = ajaxUtil;
export const MULTI_GALLERY_SELECT_TOGGLE_EVENT =
  "decoratev2:multi-gallery-select-toggle";
const COLLAPSE_SETTLE_DELAY = 260;

const { Option } = Select;

window.__cached_gallery_list = null;

export default function MultiGallerySelect(props) {
  const { value, onChange, mode, allowAllSelectAndDeselect } = props;
  const [list, setList] = useState([]);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const selectRef = useRef(null);
  const openTimerRef = useRef(null);

  const isMultiple = mode !== "single";
  const currentValueList = Array.isArray(value)
    ? value
    : value || value === 0
      ? [value]
      : [];
  const currentValueSet = new Set(
    currentValueList.map((it) => {
      if (it && typeof it === "object") {
        return `${it.key || it.value || it.label || ""}`;
      }
      return `${it}`;
    })
  );
  const allGalleryValueList = list.map((one) => `${one.key}`);
  const selectedGalleryCount = allGalleryValueList.filter((galleryId) =>
    currentValueSet.has(galleryId)
  ).length;
  const allSelected =
    allGalleryValueList.length > 0 &&
    selectedGalleryCount === allGalleryValueList.length;
  const hasSelection = currentValueList.length > 0;
  const {
    dropdownRender: customDropdownRender,
    ...restSelectProps
  } = props.selectProps || {};

  const getList = async function () {
    const gallery = window.decorateGetGallery();
    const res =
      window.__cached_gallery_list ||
      (await ajaxv2({
        url: DOMAIN_CURRENT + `/api/v2/gallery/${gallery.id}/childlist`,
      }));
    window.__cached_gallery_list = res;
    let list = (res || []).map((it) => ({
      key: it.id,
      name: it.name,
      img_count: it.img_count,
      check: it.check,
      privacy: it.privacy,
    }));
    if (list.length <= 0) {
      list.unshift({
        key: gallery.id,
        name: gallery.name,
        img_count: gallery.img_count,
        check: gallery.check,
        privacy: gallery.privacy,
      });
    }
    setList(list);
  };

  useEffect(() => {
    getList();
    return () => {
      if (openTimerRef.current) {
        clearTimeout(openTimerRef.current);
        openTimerRef.current = null;
      }
      window.__cached_gallery_list = null;
    };
  }, []);

  const clearPendingOpenTimer = () => {
    if (openTimerRef.current) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  };

  const renderGalleryName = (one) => (
    <React.Fragment>
      {one.check ? <Tag color="red" style={{
        color: 'darkred',
      }}>审核</Tag> : null}
      {one.privacy !== "normal" ? <Tag color="orange">私密</Tag> : null}
      <Tag>{one.img_count}张</Tag>
      {one.name}
    </React.Fragment>
  );

  const notifyDecorateProperties = (open) => {
    if (typeof window === "undefined" || typeof window.dispatchEvent !== "function") {
      return;
    }
    window.dispatchEvent(
      new CustomEvent(MULTI_GALLERY_SELECT_TOGGLE_EVENT, {
        detail: { open, source: "MultiGallerySelect" },
      })
    );
  };

  const shouldDelayDropdownOpen = () =>
    isMultiple && typeof window !== "undefined" && window.innerHeight <= 900;

  const openDropdownAfterCollapse = () => {
    clearPendingOpenTimer();
    openTimerRef.current = setTimeout(() => {
      setDropdownVisible(true);
      if (selectRef.current && typeof selectRef.current.focus === "function") {
        selectRef.current.focus();
      }
      openTimerRef.current = null;
    }, COLLAPSE_SETTLE_DELAY);
  };

  const handleDropdownVisibleChange = (open) => {
    if (!open) {
      clearPendingOpenTimer();
      setDropdownVisible(false);
      return;
    }

    if (shouldDelayDropdownOpen()) {
      setDropdownVisible(false);
      notifyDecorateProperties(true);
      openDropdownAfterCollapse();
      return;
    }

    clearPendingOpenTimer();
    setDropdownVisible(true);
  };

  const preventDropdownAction = (e) => {
    if (e && typeof e.preventDefault === "function") {
      e.preventDefault();
    }
    if (e && typeof e.stopPropagation === "function") {
      e.stopPropagation();
    }
  };

  const handleSelectAll = (e) => {
    preventDropdownAction(e);
    onChange && onChange(allGalleryValueList);
    setDropdownVisible(true);
  };

  const handleDeselectAll = (e) => {
    preventDropdownAction(e);
    onChange && onChange([]);
    setDropdownVisible(true);
  };

  const renderActionLink = ({
    disabled,
    onClick,
    children,
  }) => {
    return (
      <span
        onMouseDown={preventDropdownAction}
        onClick={disabled ? preventDropdownAction : onClick}
        style={{
          color: disabled ? "#bfbfbf" : "#1890ff",
          cursor: disabled ? "not-allowed" : "pointer",
          marginLeft: 12,
        }}
      >
        {children}
      </span>
    );
  };

  const dropdownRender = (menu) => {
    const renderedMenu = customDropdownRender
      ? customDropdownRender(menu)
      : menu;

    if (!allowAllSelectAndDeselect || !isMultiple) {
      return renderedMenu;
    }

    return (
      <div>
        <div
          onMouseDown={preventDropdownAction}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 12px",
            borderBottom: "1px solid #f0f0f0",
            background: "#fff",
          }}
        >
          <span style={{ fontSize: 12, color: "#999" }}>
            已选 {selectedGalleryCount} / {allGalleryValueList.length}
          </span>
          <span style={{ fontSize: 12, whiteSpace: "nowrap" }}>
            {renderActionLink({
              disabled: allSelected || allGalleryValueList.length === 0,
              onClick: handleSelectAll,
              children: "全选",
            })}
            {renderActionLink({
              disabled: !hasSelection,
              onClick: handleDeselectAll,
              children: "全不选",
            })}
          </span>
        </div>
        {renderedMenu}
      </div>
    );
  };

  return (
    <Select
      ref={selectRef}
      mode={isMultiple ? "multiple" : undefined}
      style={{ width: props.width }}
      value={value}
      open={dropdownVisible}
      onChange={onChange}
      onDropdownVisibleChange={handleDropdownVisibleChange}
      dropdownRender={dropdownRender}
      showSearch
      optionFilterProp="label"
      filterOption={(input, option) =>{        
        return `${option?.label||option?.props?.label}`.toLowerCase().includes(input.toLowerCase());
      }
        
      }
      tokenSeparators={[","]}
      {...restSelectProps}
    >
      {list.map((one) => (
        <Option key={`${one.key}`} value={`${one.key}`} label={one.name}>
          {renderGalleryName(one)}
        </Option>
      ))}
    </Select>
  );
}


// WEBPACK FOOTER //
// ./src/components/Select/MultiGallerySelect.js